import { AiOutlinePlusCircle, AiFillPlusCircle } from "react-icons/ai";
import { Position, Handle } from "reactflow";
import { useContext, useState, useEffect, useRef, useMemo } from "react";
import { NodeInfoModal } from "./NodeInfoModal";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { useDrop, useDrag } from "react-dnd";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { createPortal } from "react-dom";
import { ValueSelectionModal } from "./AvailableValueSelector";
import { FiTrash2, FiPlus, FiChevronUp, FiChevronDown } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { FaVial } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getPathFromMainItem } from "../../../../../Navigation/NavUtils";
import { MainItemType } from "../../../../../../models/WorkflowModel";
import { classifyAll } from "../../../../../MainContent/DeasyConfig/ConfigElements/useJobProgress";
import { metadataService, tagService } from "../../../../../../services/api";
import { nodePathToConditions } from "../GraphUtils";
import {
  findNodeStats,
  RerunConfirmationModal,
  DeleteConfirmationModal,
  handleConfirmNodeDelete,
  DeleteValueNodeModal,
} from "./GraphNodeUtils";
import TagEditor from "../../../../SavedTags/components/TagEditor/TagEditor";
import { StandardizeValuesPanel } from "../Standardization/StandardizeValuesPanel";
import * as Sentry from "@sentry/react";
import {
  canMoveNodeToTarget,
  getNewConditionsFromNodes,
} from "../../DataDiscovery/ExtractMetadata/components/Utils";
import CreateDataSlice from "../../../../../utils/CreateDataSlice";
import { Tooltip } from "@mui/material";
import { ValueActivationModal, getActiveValues } from "./ValueActivationModal";
import { dataSliceService } from "../../../../../../services/api";
import { toast } from "react-hot-toast";

// Custom node components
export const TagNode = ({ data }) => {
  const {
    updatedTags,
    setUpdatedTags,
    savedTags,
    deasyUserConfig,
    vdbLastActiveProfileName,
    llmLastActiveProfileName,
    deasyApiKey,
    selectedDataSlice,
    setSelectedDataSlice,
    totalActiveFiles,
    setStartingTagInTS,
    setActivePage,
    setSavedTags,
  } = useContext(BaseContext);
  const navigate = useNavigate();
  const {
    selectedDiscoveryGraph,
    hierarchyStats,
    uniqueTags,
    graphLayoutDirection,
    highlightedLevel,
    discoveryGraphData,
    graphViewMode,
  } = useContext(GraphContext);
  const showControls = !data.hideControls;

  // Find the tag's details from savedTags
  const tagDetails = savedTags.find((tag) => tag.name === data.label);
  const tagValues = useMemo(
    () => tagDetails?.available_values || [],
    [tagDetails],
  );

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTunedModal, setShowTunedModal] = useState(false);
  const [showClearValuesModal, setShowClearValuesModal] = useState(false);
  const [showRerunOptions, setShowRerunOptions] = useState(false);
  const rerunButtonRef = useRef(null);
  const rerunDropdownRef = useRef(null);
  const [showStandardizeDropdown, setShowStandardizeDropdown] = useState(false);
  const standardizeButtonRef = useRef(null);
  const [tagStats, setTagStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeout = useRef(null);
  const [tagBody, setTagBody] = useState({});
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);
  const [convertingToCategorical, setConvertingToCategorical] = useState(false);

  const [showActionMenu, setShowActionMenu] = useState(false);
  const actionButtonRef = useRef(null);
  const actionMenuTimeout = useRef(null);
  const [showValueActivationModal, setShowValueActivationModal] =
    useState(false);

  // Calculate the level of this tag node
  const tagLevel = useMemo(() => {
    if (!data.nodePath) return 1;
    return Math.floor(data.nodePath.length / 2) + 1;
  }, [data.nodePath]);

  // Check if this node is at the highlighted level
  const isHighlighted = highlightedLevel && tagLevel === highlightedLevel;

  // Calculate active values count
  const activeValues = useMemo(() => {
    return getActiveValues(discoveryGraphData, data.nodePath);
  }, [discoveryGraphData, data.nodePath]);

  const activeValueCount = activeValues.length;
  const totalValueCount = tagValues.filter((v) => v !== "<ANY>").length;

  const isOpenEndedTag = useMemo(() => {
    if (activeValueCount > 0) {
      return false;
    }

    let hasAnyInGraph = false;
    if (discoveryGraphData && data.nodePath) {
      try {
        let current = discoveryGraphData;
        for (const pathItem of data.nodePath) {
          current = current[pathItem];
          if (!current) {
            hasAnyInGraph = false;
            break;
          }
        }
        if (current) {
          hasAnyInGraph = Object.keys(current).includes("<ANY>");
        }
      } catch {
        hasAnyInGraph = false;
      }
    }

    return (
      tagValues.includes("<ANY>") || tagValues.length === 0 || hasAnyInGraph
    );
  }, [activeValueCount, discoveryGraphData, data.nodePath, tagValues]);

  const hasInactiveValues =
    activeValueCount < totalValueCount && totalValueCount > 0;

  // Function to build conditions from node path (excluding current tag, only parent conditions)
  const buildConditionsFromNodePath = (nodePath) => {
    if (!nodePath || nodePath.length === 0) {
      // Root level tag - no parent conditions, return null to indicate no filtering needed
      return null;
    }

    // Get parent conditions from the existing nodePath (exclude current tag)
    // Create a dummy node to get parent conditions
    const parentNode = {
      data: {
        nodePath: nodePath,
        label: "dummy",
      },
    };

    const parentConditions = getNewConditionsFromNodes([parentNode]);

    // Return only parent conditions, not including current tag
    return parentConditions;
  };

  useEffect(() => {
    const tagBody = savedTags.find((tag) => tag.name === data.label);
    setTagBody(tagBody);
    if (!tagBody) {
      setIsLoading(true);
    }
  }, [savedTags, data.label]);

  const nodeStats = findNodeStats(hierarchyStats, data.nodePath);
  const {
    vdbmConfig: { Configs: vdbmConfigs },
    llmConfig: { LastActive: llmLastActive },
  } = deasyUserConfig;
  const vectorDBConfiguration = vdbmConfigs[vdbLastActiveProfileName];

  const [{ isOver }, drop] = useDrop({
    accept: "TAG",
    drop: (item, monitor) => {
      const tag = item.tag;
      if (tag) {
        data.onAddNode(data, "tag");
        data.handleAddNodeApplied(data, tag);
      }
      return { dropped: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const [showValueModal, setShowValueModal] = useState(false);

  const questionTagUpdated = updatedTags[data.label];

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [tagInEditor, setTagInEditor] = useState(null);
  const [isTagEditorCollapsed, setIsTagEditorCollapsed] = useState(false);
  const [activeTagEditorSection, setActiveTagEditorSection] =
    useState("general");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: "MOVE_TAG_NODE",
    item: {
      nodeId: data.id,
      nodeData: data,
      nodeType: "tag",
      nodePath: data.nodePath,
      label: data.label,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragDropRef = (el) => {
    drag(el);
    drop(el);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        rerunDropdownRef.current &&
        !rerunDropdownRef.current.contains(event.target) &&
        !rerunButtonRef.current.contains(event.target)
      ) {
        setShowRerunOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isHovered) {
      setShowRerunOptions(false);
    }
  }, [isHovered]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (actionMenuTimeout.current) {
        clearTimeout(actionMenuTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        standardizeButtonRef.current &&
        !standardizeButtonRef.current.contains(event.target)
      ) {
        setShowStandardizeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch tag statistics for standardization
  const fetchTagStatistics = async () => {
    try {
      if (!data.label) return;

      setIsLoadingStats(true);

      const response = await metadataService.getTagStatistics(
        vdbLastActiveProfileName,
        [data.label],
        deasyApiKey,
        nodePathToConditions(data.nodePath),
        selectedDataSlice?.id || null,
      );

      setTagStats(response.data.tag_statistics[data.label]);
      return response.data.tag_statistics[data.label];
    } catch (error) {
      console.error("Error fetching tag statistics:", error);
      throw error;
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Fetch tag statistics when standardize dropdown is opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (showStandardizeDropdown && !tagStats) {
      fetchTagStatistics();
    }
  }, [showStandardizeDropdown, tagStats]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tagBody) {
      setTagInEditor(tagBody);
    }
  }, [tagBody]);

  const handleRerun = async (option) => {
    const fetchFileCount = async (data) => {
      const newConditions = getNewConditionsFromNodes([data]);
      const response = await dataSliceService.getDatasliceFileCount(
        vectorDBConfiguration.name,
        newConditions,
        selectedDataSlice?.id || null,
        deasyApiKey,
      );
      return response.data.file_count;
    };
    let numberOfFilesToClassify =
      option === "node" ? nodeStats?.file_count || 0 : totalActiveFiles || 0;
    if (option === "node" && numberOfFilesToClassify === 0) {
      const node = {
        data: data,
      };
      const fileCount = await fetchFileCount(node);
      numberOfFilesToClassify = fileCount;
    }

    setConfirmationDetails({
      tagCount: 1,
      tags: [data.label],
      fileCount: numberOfFilesToClassify,
      isRootSelection: option !== "node",
      option,
    });
    setShowConfirmDialog(true);
    setShowRerunOptions(false);
  };

  const handleConfirmedRerun = () => {
    setShowConfirmDialog(false);
    if (!confirmationDetails) return;
    setShowRerunOptions(false);
    try {
      classifyAll(
        vdbLastActiveProfileName, // vdbmLastActive
        llmLastActive, // llmLastActive
        deasyApiKey, // deasyApiKey
        data, // selectedNodeData
        totalActiveFiles, // vdbFilesCount
        { [data.label]: tagBody }, // tags
        null, // hierarchy
        confirmationDetails.option === "node", // useConditions
        true, // overwrite
        selectedDataSlice?.id || null, // dataSliceId
      );
      // Trigger the MiniProgressTracker grace period to look for the new job
      if (window.startTrackerGracePeriod) {
        window.startTrackerGracePeriod(5 * 60000); // 5 minute grace period
      }
    } catch (error) {
      console.error("Error rerunning:", error);
      Sentry.captureException(error);
    }
  };

  const handleCloseEditor = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        setIsEditing(false);
        setHasUnsavedChanges(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  // Handle test tag functionality
  const handleTestTag = (e) => {
    e.stopPropagation();

    // Find the tag in savedTags
    const tag = savedTags.find(
      (t) => t.tag_id === data.label || t.name === data.label,
    );
    if (tag) {
      // Set the tag in editor
      setStartingTagInTS(tag);
      // Switch to tagging studio screen
      setActivePage(MainItemType.METADATA_STUDIO);

      // Get the current data source name
      const dataSource =
        deasyUserConfig.vdbmConfig?.Configs?.[
          deasyUserConfig.vdbmConfig.LastActive
        ];
      const dataSourceName = dataSource?.name || "none-selected";

      // Navigate to the tagging studio page
      const path = getPathFromMainItem(
        MainItemType.METADATA_STUDIO,
        dataSourceName,
      );
      navigate(path, {
        state: { page: MainItemType.METADATA_STUDIO },
      });
    } else {
      toast.error("Tag not found for testing");
    }
  };

  // Always in tag-tag mode

  useEffect(() => {
    setIsLoading(!tagBody);
  }, [tagBody]);

  // Handler for when a child tag should be added to a value (in tag-tag mode)
  const handleAddChildTag = (selectedValue) => {
    if (data.onAddNodeToValue) {
      data.onAddNodeToValue(data, selectedValue);
    }
  };

  const convertToCategorical = async () => {
    try {
      setConvertingToCategorical(true);
      const response = await metadataService.getStandardizeTagDistribution(
        data.label,
        vdbLastActiveProfileName,
        deasyApiKey,
        selectedDataSlice?.id || null,
      );
      const valueMapping = {};
      for (const value in response.data.tag_distribution) {
        valueMapping[value] = [value];
      }

      await metadataService.standardizeMetadataDB(
        data.label,
        valueMapping,
        deasyApiKey,
        vdbLastActiveProfileName,
        llmLastActiveProfileName,
      );

      const updatedTagData = {
        ...tagDetails,
        available_values: Object.keys(valueMapping),
      };
      await tagService.updateTag(updatedTagData, deasyApiKey);

      setSavedTags((prevTags) =>
        prevTags.map((tag) =>
          tag.tag_id === tagDetails.tag_id
            ? {
                ...tag,
                available_values: Object.keys(valueMapping),
              }
            : tag,
        ),
      );

      toast.success(
        `Tag converted to categorical with ${Object.keys(valueMapping).length} values`,
      );
    } catch (error) {
      console.error("Error converting to categorical:", error);
      toast.error("Error converting to categorical");
    } finally {
      setConvertingToCategorical(false);
    }
  };

  return (
    <div
      ref={dragDropRef}
      className={`flex flex-col items-center p-4 ${
        isOver ? "bg-gray-100 rounded-lg p-6" : ""
      } overflow-visible group ${isDragging ? "opacity-50" : ""}`}
      style={{ position: "relative", zIndex: 0 }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => {
        // Allow hover regardless of loading state
        clearTimeout(hoverTimeout.current);
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        hoverTimeout.current = setTimeout(() => setIsHovered(false), 300);
      }}
    >
      {showStandardizeDropdown &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1000]"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
          >
            <StandardizeValuesPanel
              isOpen={showStandardizeDropdown}
              onClose={() => setShowStandardizeDropdown(false)}
              valueDistribution={tagStats?.value_distribution}
              onValueSelect={(values, stats) => {}}
              buttonRef={standardizeButtonRef}
              tagsDetails={tagDetails}
              className="w-full"
            />
          </div>,
          document.body,
        )}

      <RerunConfirmationModal
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmedRerun}
        data={data}
        tagBody={tagBody}
        nodeStats={nodeStats}
        vdbFilesCount={totalActiveFiles}
        confirmationDetails={confirmationDetails}
      />

      <div
        className="flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-[500px] relative">
          {/* Add drag handle indicator */}
          <div
            className="absolute -left-6 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            title="Drag to move"
          >
            <MdDragIndicator size={20} />
          </div>

          <div
            className={`rounded-md border px-4 py-3 transition-colors shadow-md min-w-[260px]
              ${questionTagUpdated && "border-orange-300 bg-orange-100"}
              ${
                !questionTagUpdated &&
                data.label !== "<ANY>" &&
                uniqueTags &&
                uniqueTags.includes(data.label)
                  ? "border-primary/60 shadow-primary/10 bg-gradient-to-br from-primary/5 to-primary/10 font-medium"
                  : "border-gray-600 bg-gray-200"
              }
              ${isDragging ? "ring-2 ring-gray-400 opacity-70" : ""}
              ${isLoading ? "opacity-60" : ""}
              ${isHighlighted ? "ring-2 ring-primary ring-offset-2 bg-primary/10" : ""}
              group-hover:shadow-lg cursor-grab
            `}
          >
            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10 rounded-md">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {questionTagUpdated && (
              <div className="absolute -top-2.5 -right-2.5">
                <span className="relative flex h-4 w-4">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-orange-500"></span>
                </span>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {/* Header with tag name and controls */}
              <div className="flex flex-row justify-between items-center gap-3 text-left">
                <div className="flex items-center gap-2">
                  <Tooltip
                    title={
                      tagDetails?.description || "No description available"
                    }
                    arrow
                    placement="top"
                    className="cursor-help"
                  >
                    <div className="font-medium text-gray-800 text-[2rem] text-left break-words">
                      {data.label === "<ANY>" ? "Open-ended" : data.label}
                    </div>
                  </Tooltip>
                  {hasInactiveValues && (
                    <Tooltip
                      title="Custom value selection"
                      arrow
                      placement="top"
                    >
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) setIsEditing(true);
                    }}
                    className={`p-1.5 rounded-md ${
                      isLoading
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-10 text-xl"
                    } transition-all duration-150 ease-in-out flex-shrink-0`}
                    title={isLoading ? "Loading tag data..." : "Edit Tag"}
                    disabled={isLoading}
                  >
                    <svg
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>

                  {/* Delete button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) setShowDeleteModal(true);
                    }}
                    className={`p-1.5 rounded-md ${
                      isLoading
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                    } transition-all duration-150 ease-in-out flex-shrink-0`}
                    title={isLoading ? "Loading..." : "Delete Tag"}
                    disabled={isLoading}
                  >
                    <FiTrash2 size={25} />
                  </button>
                </div>
              </div>

              {/* Active Constraints Section */}
              {graphViewMode === "tag-tag" && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1">
                      Target values
                      <button
                        className="text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
                        title="Edit target values"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowValueActivationModal(true);
                        }}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </h4>
                  </div>

                  {/* Show active values or open-ended status */}
                  {totalValueCount === 0 ||
                  isOpenEndedTag ||
                  activeValueCount === 0 ? (
                    <div className="text-sm text-gray-700 text-left">
                      <span className="font-medium">None:</span> Tag output can
                      take <span className="font-medium">any</span> value
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600 text-left">
                        {activeValueCount} of {totalValueCount} values active -
                        only these will be used for extraction:
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {activeValues.slice(0, 3).map((value, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200"
                          >
                            {value}
                          </span>
                        ))}
                        {activeValueCount > 3 && (
                          <span className="text-xs text-gray-500 px-2 py-0.5">
                            +{activeValueCount - 3} more
                          </span>
                        )}
                      </div>

                      {/* Show inactive values if any */}
                      {hasInactiveValues && (
                        <div className="pt-2 border-t border-gray-200">
                          <div className="text-xs text-gray-500 mb-2 text-left">
                            {totalValueCount - activeValueCount} inactive values
                            available to add:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {tagValues
                              .filter(
                                (v) =>
                                  v !== "<ANY>" && !activeValues.includes(v),
                              )
                              .slice(0, 3)
                              .map((value, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    data.onAddAvailableValue?.(data, value);
                                  }}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 hover:border-gray-400 transition-colors cursor-pointer"
                                  title="Click to activate this value"
                                >
                                  {value}
                                  <svg
                                    className="w-3 h-3 ml-1"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="16" />
                                    <line x1="8" y1="12" x2="16" y2="12" />
                                  </svg>
                                </button>
                              ))}
                            {totalValueCount - activeValueCount > 3 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowValueActivationModal(true);
                                }}
                                className="text-xs text-gray-500 px-2 py-0.5 hover:text-gray-700 cursor-pointer"
                              >
                                +{totalValueCount - activeValueCount - 3}{" "}
                                more...
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        {showControls && (
          <div className="mt-2 flex flex-row gap-1.5 align-baseline justify-start w-full">
            {/* Actions Button */}
            <div
              className="relative action-menu-container"
              onMouseEnter={() => {
                clearTimeout(actionMenuTimeout.current);
                setShowActionMenu(true);
              }}
              onMouseLeave={() => {
                actionMenuTimeout.current = setTimeout(
                  () => setShowActionMenu(false),
                  300,
                );
              }}
            >
              <Tooltip arrow placement="bottom">
                <button
                  ref={actionButtonRef}
                  className="flex items-center gap-1.5 text-primary hover:text-primary-dark
                           bg-white hover:bg-gray-50 border border-gray-300 hover:border-primary 
                           shadow-sm hover:shadow-md px-2.5 py-1.5 rounded-md transition-all duration-150"
                >
                  <FiPlus className="w-4 h-4" />
                  <span className="text-[1.5rem] font-medium">Actions</span>
                  <svg
                    className={`w-3 h-3 transition-transform ${showActionMenu ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </Tooltip>
            </div>

            {/* Info Button */}
            <Tooltip title="View statistics" arrow placement="bottom">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal(true);
                }}
                className="flex items-center gap-1.5 text-secondary hover:text-green-700
                         bg-white hover:bg-gray-50 border border-gray-300 hover:border-green-600 
                         shadow-sm hover:shadow-md px-2.5 py-1.5 rounded-md transition-all duration-150"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <span className="text-[1.5rem] font-medium">View Outputs</span>
              </button>
            </Tooltip>

            {/* Action Menu Dropdown */}
            {showActionMenu &&
              createPortal(
                <div
                  className="fixed w-64 bg-white border border-gray-200 shadow-lg rounded-lg py-2 z-[1000] animate-fadeIn"
                  style={{
                    top: actionButtonRef.current
                      ? actionButtonRef.current.getBoundingClientRect().bottom +
                        4
                      : 0,
                    left: actionButtonRef.current
                      ? actionButtonRef.current.getBoundingClientRect().left
                      : 0,
                  }}
                  onMouseEnter={() => {
                    clearTimeout(actionMenuTimeout.current);
                    setShowActionMenu(true);
                  }}
                  onMouseLeave={() => {
                    actionMenuTimeout.current = setTimeout(
                      () => setShowActionMenu(false),
                      300,
                    );
                  }}
                >
                  {/* Run Tag - with submenu */}
                  <div className="relative">
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLoading) setShowRerunOptions(!showRerunOptions);
                      }}
                      disabled={isLoading}
                    >
                      <svg
                        className="w-4 h-4 text-primary"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polygon
                          points="10 8 16 12 10 16 10 8"
                          fill="currentColor"
                          stroke="none"
                        />
                      </svg>
                      <div className="flex-1">
                        <div className="font-medium">Run Tag</div>
                        <div className="text-xs text-gray-500">
                          Re-run tag classification
                        </div>
                        {questionTagUpdated && (
                          <div className="inline-flex items-center gap-1 mt-1">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            <span className="text-xs text-orange-600">
                              Updated
                            </span>
                          </div>
                        )}
                      </div>
                      <svg
                        className={`w-4 h-4 transition-transform ${showRerunOptions ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Run options submenu */}
                    {showRerunOptions && (
                      <div className="ml-8 border-l border-gray-200 pl-4 py-1">
                        {/* Only show "This node only" if not a root node */}
                        {data.nodePath && data.nodePath.length > 1 && (
                          <button
                            onClick={() => handleRerun("node")}
                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-2 rounded"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <circle cx="12" cy="12" r="4"></circle>
                            </svg>
                            This node only
                          </button>
                        )}
                        <button
                          onClick={() => handleRerun("connector")}
                          className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-50 hover:text-gray-800 transition-colors flex items-center gap-2 rounded"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 14v6"></path>
                            <path d="M6 4v6"></path>
                            <path d="M12 13a5 5 0 0 0 5-5V4"></path>
                            <path d="M12 19a5 5 0 0 1-5-5V4"></path>
                          </svg>
                          All files
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Create Nested Tag - disabled for open-ended tags */}
                  <button
                    className={`w-full text-left px-4 py-2 flex items-center gap-3 text-sm ${
                      isOpenEndedTag
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isOpenEndedTag) {
                        toast.error(
                          "Open-ended tags cannot have nested tags. Define specific values first.",
                        );
                        return;
                      }
                      setShowValueModal(true);
                      setShowActionMenu(false);
                    }}
                    disabled={isOpenEndedTag}
                  >
                    <svg
                      className={`w-4 h-4 ${isOpenEndedTag ? "text-gray-400" : "text-primary"}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                    </svg>
                    <div>
                      <div className="font-medium">Create Nested Tag</div>
                      <div className="text-xs text-gray-500">
                        {isOpenEndedTag
                          ? "Not available for open-ended tags"
                          : "Build hierarchical tag structure"}
                      </div>
                    </div>
                  </button>

                  {/* Divider */}
                  <div className="border-t border-gray-100 my-1"></div>

                  {/* Test Tag */}
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900"
                    onClick={(e) => {
                      handleTestTag(e);
                      setShowActionMenu(false);
                    }}
                  >
                    <FaVial className="w-4 h-4 text-gray-600" />
                    <div>
                      <div className="font-medium">Test Tag</div>
                      <div className="text-xs text-gray-500">
                        Test tag performance in studio
                      </div>
                    </div>
                  </button>

                  {/* Standardize Values */}
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (isLoading) return;
                      if (!tagStats) {
                        try {
                          await fetchTagStatistics();
                        } catch (error) {
                          console.error(
                            "Error fetching tag statistics:",
                            error,
                          );
                        }
                      }
                      setShowStandardizeDropdown(true);
                      setShowActionMenu(false);
                    }}
                    disabled={isLoading || isLoadingStats}
                  >
                    {isLoadingStats ? (
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg
                        className="w-4 h-4 text-gray-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 10H3"></path>
                        <path d="M21 6H3"></path>
                        <path d="M21 14H3"></path>
                        <path d="M21 18H3"></path>
                      </svg>
                    )}
                    <div>
                      <div className="font-medium">Standardize Values</div>
                      <div className="text-xs text-gray-500">
                        Clean up value variations
                      </div>
                    </div>
                  </button>

                  {/* Create Data Slice */}
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCreateDataSlice(true);
                      setShowActionMenu(false);
                    }}
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 2L2 7l10 5 10-5-10-5z" />
                      <path d="M2 17l10 5 10-5" />
                      <path d="M2 12l10 5 10-5" />
                    </svg>
                    <div>
                      <div className="font-medium">Create Data Slice</div>
                      <div className="text-xs text-gray-500">
                        {nodeStats?.file_count
                          ? `${nodeStats.file_count.toLocaleString()} files available`
                          : "Filter data by this tag"}
                      </div>
                    </div>
                  </button>

                  {/* Turn Output Values into 1/1 Available Values */}
                  <button
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-sm text-gray-700 hover:text-gray-900 ${
                      convertingToCategorical ||
                      (tagDetails?.available_values &&
                        tagDetails?.available_values.length > 0)
                        ? "cursor-not-allowed opacity-50"
                        : ""
                    }`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await convertToCategorical();
                    }}
                    disabled={
                      convertingToCategorical ||
                      (tagDetails?.available_values &&
                        tagDetails?.available_values.length > 0)
                    }
                    title={
                      convertingToCategorical ||
                      (tagDetails?.available_values &&
                        tagDetails?.available_values.length > 0)
                        ? "Tag already has available values"
                        : ""
                    }
                  >
                    {convertingToCategorical ? (
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 text-gray-600"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />
                          <path
                            d="M10 7l4 5m0 0l-4 5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div>
                          <div className="font-medium">
                            Convert to Categorical
                          </div>
                          <div className="text-xs text-gray-500">
                            Turn all output values into categorical values
                          </div>
                        </div>
                      </>
                    )}
                  </button>

                  {/* Move Tag - only show if not at root */}
                </div>,
                document.body,
              )}
          </div>
        )}

        {showValueModal && (
          <ValueSelectionModal
            onClose={() => setShowValueModal(false)}
            onSelect={(value) => {
              data.onAddAvailableValue?.(data, value);
              setShowValueModal(false);
            }}
            onAddNew={(value) => {
              data.onAddAvailableValue?.(data, value);
              setShowValueModal(false);
            }}
            availableValues={tagValues}
            selectedTag={data.label}
            allowChildTagAddition={true}
            tagData={data}
            onAddChildTag={handleAddChildTag}
          />
        )}

        <Handle
          type="target"
          position={
            graphLayoutDirection === "horizontal" ? Position.Left : Position.Top
          }
          isConnectable={false}
        />
        <Handle
          type="source"
          position={
            graphLayoutDirection === "horizontal"
              ? Position.Right
              : Position.Bottom
          }
          isConnectable={false}
        />
      </div>

      {showModal && (
        <NodeInfoModal
          nodeData={data}
          onClose={() => setShowModal(false)}
          setUpdatedTags={setUpdatedTags}
          vectorDBConfiguration={vectorDBConfiguration}
          deasyApiKey={deasyApiKey}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          nodeName={data.label}
          onConfirm={() => {
            handleConfirmNodeDelete(setShowDeleteModal, data);
          }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {showTunedModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg p-6 max-w-sm w-96 shadow-xl">
              <h3 className="text-lg font-semibold mb-4">Fine-tuned Tag</h3>
              <p className="mb-6 text-gray-600">
                This tag has been fine-tuned to improve its accuracy and
                performance.
              </p>
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 rounded-md text-gray-600 hover:bg-gray-50 border border-gray-200"
                  onClick={() => setShowTunedModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showClearValuesModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowClearValuesModal(false);
              }
            }}
          >
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-semibold mb-2">
                Clear Output Values
              </h3>
              <div className="mb-6 space-y-4">
                <p className="text-gray-600">
                  Are you sure you want to clear all output values for the tag "
                  {data.label}"?
                </p>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium mb-2">
                    This will have the following effects:
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-700 space-y-1.5">
                    <li>
                      All categories will be removed from the schema for this
                      tag
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-md text-gray-600 hover:bg-gray-50 border border-gray-200"
                  onClick={() => setShowClearValuesModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-md bg-red-500 text-white hover:bg-red-600"
                  onClick={() => {
                    data.onClearValues(data);
                    setShowClearValuesModal(false);
                  }}
                >
                  Clear Values
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {isEditing &&
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] animate-fadeIn"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseEditor();
            }}
          >
            <div
              className="bg-white relative rounded-lg p-6 w-[78vw] max-h-[90vh] overflow-y-auto mx-4 animate-slideIn"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Edit Tag: {data.label}
                </h2>
                <button
                  onClick={handleCloseEditor}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  ✕
                </button>
              </div>

              <TagEditor
                collapseDirection="horizontal"
                disableCollapse={true}
                showHeader={false}
                tagInEditor={tagInEditor}
                setTagInEditor={setTagInEditor}
                isTagEditorCollapsed={isTagEditorCollapsed}
                setIsTagEditorCollapsed={setIsTagEditorCollapsed}
                activeTagEditorSection={activeTagEditorSection}
                setActiveTagEditorSection={setActiveTagEditorSection}
                context="modal"
                editingExistingTag={true}
                onSave={() => {
                  setHasUnsavedChanges(false);
                  setIsEditing(false);
                  // Refresh the node's data if needed
                  if (data.onRefresh) {
                    data.onRefresh();
                  }
                }}
                onFormChange={() => setHasUnsavedChanges(true)}
              />
            </div>
          </div>,
          document.body,
        )}

      {showValueActivationModal && (
        <ValueActivationModal
          isOpen={showValueActivationModal}
          onClose={() => setShowValueActivationModal(false)}
          tagData={{
            label: data.label,
            nodePath: data.nodePath,
            availableValues: tagValues, // Add the full list of available values from the saved tag
          }}
          onUpdate={(newActiveValues) => {
            // Optional: Add any additional handling after values are updated
            console.log(
              `Updated target values for ${data.label}:`,
              newActiveValues.size,
            );
          }}
        />
      )}

      {showCreateDataSlice &&
        createPortal(
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => setShowCreateDataSlice(false)}
            conditions={buildConditionsFromNodePath(data.nodePath)}
            fileCount={nodeStats?.file_count || 0}
            discoveryGraphData={discoveryGraphData}
            selectedDiscoveryGraph={selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              setSelectedDataSlice(dataSlice);
            }}
            isGraphMode={true}
            isAllFiles={false}
          />,
          document.body,
        )}

      <style jsx global>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out forwards;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out forwards;
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-slideIn {
          animation: slideIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export const ValueNode = ({ data }) => {
  const {
    selectedNodeData,
    hierarchyStats,
    selectedNodes,
    setSelectedNodes,
    activeOverlappingNode,
    discoveryGraphData,
    selectedDiscoveryGraph,
    graphLayoutDirection,
  } = useContext(GraphContext);
  const { isConfigRefreshing, setSelectedDataSlice } = useContext(BaseContext);
  const nodeIsSelected =
    selectedNodeData !== null && selectedNodeData?.id === data.id;

  const showControls = !data.hideControls;
  const isSelected = selectedNodes.some((node) => node.id === data.id);

  const nodeStats = findNodeStats(hierarchyStats, data.nodePath);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [childNodes, setChildNodes] = useState([]);
  const [valueNodesCollapsed, setValueNodesCollapsed] = useState(false);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);

  const hasChildren = useMemo(() => {
    if (!discoveryGraphData || !data.nodePath) return false;

    try {
      let current = discoveryGraphData;
      for (const segment of data.nodePath) {
        current = current[segment];
      }
      return Object.keys(current).some((key) => key !== "TagAvailableValues");
    } catch {
      return false;
    }
  }, [discoveryGraphData, data.nodePath]);

  const handleRemoveClick = () => {
    // Find child nodes from the graph data
    const currentPath = data.nodePath;
    let current = discoveryGraphData;

    // Navigate to current node in the graph data
    for (const pathItem of currentPath) {
      current = current[pathItem];
    }

    // Get all child nodes
    const children = [];
    if (current && typeof current === "object") {
      Object.keys(current).forEach((key) => {
        if (key !== "TagAvailableValues") {
          children.push({
            label: key,
            path: [...currentPath, key].join("/"),
          });
        }
      });
    }

    setChildNodes(children);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    data.onRemoveAvailableValue(data);
  };

  const handleNodeClick = (e, nodeStats) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't allow selection if it's an open-ended node
    if (data.label === "<ANY>") return;

    setSelectedNodes((prev) => {
      const newSelection = [...prev];
      const nodeIndex = newSelection.findIndex((node) => node.id === data.id);

      if (nodeIndex !== -1) {
        // Remove just this node
        return newSelection.filter((node) => node.id !== data.id);
      }
      // If node is not selected, select only this node
      else {
        // Add only the clicked node
        return [
          ...newSelection,
          {
            id: data.id,
            data: {
              nodePath: data.nodePath,
              label: data.label,
            },
            type: data.type,
            stats: {
              fileCount: nodeStats?.file_count || 0,
              percentage: nodeStats?.percentage || 0,
            },
          },
        ];
      }
    });
  };

  // Add click handler to the container div to prevent bubbling
  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  // Enhanced drop functionality to accept both new tags and moving existing tags
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ["TAG", "MOVE_TAG_NODE"],
    canDrop: (item, monitor) => {
      if (monitor.getItemType() === "MOVE_TAG_NODE") {
        // Check if this is a valid target for the tag being moved
        return (
          data.nodePath && canMoveNodeToTarget(item.nodePath, data.nodePath)
        );
      }
      return true; // Default behavior for regular TAG drops
    },
    drop: (item, monitor) => {
      if (monitor.getItemType() === "MOVE_TAG_NODE") {
        // Handle moving an existing tag node
        if (data.onMoveTagNode) {
          data.onMoveTagNode(item.nodeData, data);
        }
        return { moved: true };
      } else {
        // Handle adding a new tag
        const tag = item.tag;
        if (tag) {
          data.onAddNode(data, "value");
          data.handleAddNodeApplied(data, tag);
        }
        return { dropped: true };
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop() && monitor.isOver(),
    }),
  });

  // Add useEffect for click outside handling
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Only proceed if there are any selected nodes
      if (selectedNodes.length > 0) {
        // Check if click was outside of any ValueNode containers
        const valueNodeContainers = document.querySelectorAll(
          ".value-node-container",
        );
        let clickedInside = false;
        valueNodeContainers.forEach((container) => {
          if (container.contains(e.target)) {
            clickedInside = true;
          }
        });

        if (!clickedInside) {
          // Clear all selections
          setSelectedNodes([]);
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [selectedNodes, setSelectedNodes]); // Removed data.id from dependencies since we handle all nodes

  const handleToggleValueSubtree = (e) => {
    e.stopPropagation();
    const newCollapsedState = !valueNodesCollapsed;
    setValueNodesCollapsed(newCollapsedState);

    console.log(
      "Toggling subtree visibility for:",
      data.id,
      "to:",
      newCollapsedState,
    );

    if (data.onToggleSubtreeVisibility) {
      data.onToggleSubtreeVisibility(data.id, newCollapsedState);
    } else {
      console.error("onToggleSubtreeVisibility not provided in data props");
    }
  };

  return (
    <div
      ref={drop}
      onClick={handleContainerClick}
      className={`value-node-container flex flex-col items-center p-3 ${
        isOver && canDrop
          ? "bg-green-100 border-2 border-dashed border-green-400 rounded-lg p-4"
          : isOver
            ? "bg-red-50 rounded-lg p-4"
            : ""
      }`}
      style={{
        transition: "all 0.3s ease",
      }}
      data-id={data.id}
      data-parent-id={
        data.nodePath && data.nodePath.length > 1
          ? data.nodePath.slice(0, -1).join("/")
          : null
      }
    >
      <div className="flex flex-col items-center">
        <div className="flex gap-2"></div>
        <div
          onClick={(e) => handleNodeClick(e, nodeStats)}
          className={`
            rounded-sm border py-2 min-w-[120px]
            ${data.label === "<ANY>" ? "px-4 border-dashed" : "pl-4 pr-2"}
            ${
              nodeStats?.file_count > 0 && data.label !== "<ANY>"
                ? "border-primary/40 bg-[#f0fff4]"
                : "border-gray-300 bg-white"
            }
            ${data.label === "<ANY>" ? "" : "cursor-pointer"}
            ${data.id === activeOverlappingNode ? "ring-8" : ""}
            ${
              isSelected && data.label !== "<ANY>"
                ? "ring-2 ring-primary ring-offset-2"
                : nodeStats?.file_count > 0
                  ? "hover:border-[#38a169] hover:bg-[#f0fff4]"
                  : "hover:bg-gray-50"
            }
          `}
        >
          <div className="max-w-[14vw] flex flex-row justify-between items-center gap-3 break-normal">
            <div
              className={`font-medium ${
                data.label === "<ANY>"
                  ? "text-gray-500 italic"
                  : nodeStats?.file_count > 0
                    ? "text-primary"
                    : "text-gray-600"
              } text-left flex-grow`}
            >
              {data.label === "<ANY>" ? "Open-ended" : data.label}
            </div>
            {showControls &&
              data.label !== "<ANY>" &&
              data.label !== "Not found" && (
                <button
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100
                           transition-all duration-150 ease-in-out self-start"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick();
                  }}
                >
                  {nodeIsSelected ? (
                    <FiTrash2 size={10} className="text-red-500" />
                  ) : (
                    <FiTrash2 size={10} className="text-gray-500" />
                  )}
                </button>
              )}
          </div>
          {data.label !== "<ANY>" && (
            <div
              className={`text-sm ${nodeStats?.file_count > 0 ? "text-primary" : "text-gray-500"} text-left w-full mt-1`}
            >
              {isConfigRefreshing ? (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-0.5">
                    <div
                      className={`${nodeStats?.file_count > 0 ? "font-medium" : ""} flex items-center gap-1`}
                    >
                      <span className="text-gray-500">Files:</span>
                      <span
                        className={
                          nodeStats?.file_count > 0
                            ? "text-primary font-semibold"
                            : "text-gray-600"
                        }
                      >
                        {nodeStats
                          ? nodeStats.file_count.toLocaleString()
                          : "0"}
                      </span>
                    </div>
                    <div
                      className={`${nodeStats?.file_count > 0 ? "font-medium" : ""} flex items-center gap-1`}
                    >
                      <span className="text-gray-500">Coverage:</span>
                      <span
                        className={
                          nodeStats?.file_count > 0
                            ? "text-primary font-semibold"
                            : "text-gray-600"
                        }
                      >
                        {nodeStats
                          ? nodeStats.percentage.toFixed(1) + "%"
                          : "0.0%"}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-row justify-start items-center gap-2 flex-wrap">
            {/* Create Data Slice button - only show for nodes with files */}
            {data.label !== "<ANY>" && nodeStats?.file_count > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNodeClick(e, nodeStats);
                  setShowCreateDataSlice(true);
                }}
                className="mt-2 px-3 py-1.5 text-xs bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-md hover:from-gray-600 hover:to-gray-700 transition-all duration-150 font-medium shadow-sm hover:shadow-md transform hover:scale-105 flex items-center gap-1.5"
                title="Create a data slice from this value"
              >
                <svg
                  className="w-3 h-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                Create Data Slice
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {showControls &&
            data.label !== "<ANY>" &&
            data.label !== "Not found" && (
              <button
                className={`text-primary hover:scale-110 transition-all ${
                  nodeIsSelected
                    ? "bg-primary text-white rounded-full shadow-lg animate-pulse"
                    : "hover:bg-primary/10 rounded-full"
                }`}
                onClick={(e) => data.onAddNode(data, "value")}
                title="Add child node"
              >
                {nodeIsSelected ? (
                  <AiFillPlusCircle size={28} className="p-0.5" />
                ) : (
                  <AiOutlinePlusCircle size={24} />
                )}
              </button>
            )}

          {/* Additional Create Data Slice button for quick access */}
          {showControls &&
            data.label !== "<ANY>" &&
            data.label !== "Not found" &&
            nodeStats?.file_count > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNodeClick(e, nodeStats);
                  setShowCreateDataSlice(true);
                }}
                className="text-gray-600 hover:text-gray-700 hover:scale-110 transition-all hover:bg-gray-50 rounded-full p-1.5"
                title="Create data slice from this value"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </button>
            )}
        </div>
        <Handle
          type="target"
          position={
            graphLayoutDirection === "horizontal" ? Position.Left : Position.Top
          }
          isConnectable={false}
          style={{ opacity: 0 }}
        />
        <Handle
          type="source"
          position={
            graphLayoutDirection === "horizontal"
              ? Position.Right
              : Position.Bottom
          }
          isConnectable={false}
        />
      </div>

      {/* Collapse Toggle Button */}
      {hasChildren && (
        <button
          className={`absolute -bottom-7 left-1/2 transform -translate-x-1/2 flex items-center justify-center
                     w-6 h-6 rounded-full border transition-all duration-200
                     ${
                       valueNodesCollapsed
                         ? "bg-primary/20 border-primary/30 text-primary hover:bg-primary/30 hover:border-primary/40"
                         : "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 hover:border-gray-400"
                     }
                     hover:shadow-sm group scale-100 hover:scale-110`}
          onClick={handleToggleValueSubtree}
          title={valueNodesCollapsed ? "Expand subtree" : "Collapse subtree"}
        >
          {valueNodesCollapsed ? (
            <FiChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.2" />
          ) : (
            <FiChevronUp className="w-4 h-4 transition-transform group-hover:-translate-y-0.2" />
          )}

          {/* Status indicator */}
          {valueNodesCollapsed && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full transition-opacity group-hover:opacity-75" />
          )}
        </button>
      )}

      {showCreateDataSlice &&
        createPortal(
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => setShowCreateDataSlice(false)}
            fileCount={nodeStats?.file_count || 0}
            discoveryGraphData={discoveryGraphData}
            selectedDiscoveryGraph={selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              setSelectedDataSlice(dataSlice);
              // Optionally navigate to data slice management
              // setCurrentScreen(WorkflowType.DATA_SLICE_MANAGEMENT);
            }}
            isGraphMode={true}
            isAllFiles={false}
            conditions={getNewConditionsFromNodes(selectedNodes)}
          />,
          document.body,
        )}

      <DeleteValueNodeModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        data={data}
        childNodes={childNodes}
      />
    </div>
  );
};

export const RootNode = ({ data }) => {
  const {
    loadingVdbFilesCount,
    isConfigRefreshing,
    totalActiveFiles,
    loadingTotalActiveFiles,
    setSelectedDataSlice,
  } = useContext(BaseContext);
  const {
    setSidePanelOpen,
    selectedNodeData,
    graphLayoutDirection,
    discoveryGraphData,
    selectedDiscoveryGraph,
  } = useContext(GraphContext);

  const nodeIsSelected =
    selectedNodeData !== null && selectedNodeData?.id === data.id;
  const showControls = !data.hideControls;
  const [showDataSliceDropdown, setShowDataSliceDropdown] = useState(false);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const loadingFiles =
    loadingTotalActiveFiles || isConfigRefreshing || loadingVdbFilesCount;

  const handleAddRootChild = () => {
    setSidePanelOpen(true);
    data.onAddNode(data, "root");
  };

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ["TAG", "MOVE_TAG_NODE"],
    canDrop: (item, monitor) => {
      if (monitor.getItemType() === "MOVE_TAG_NODE") {
        // Check if this is a valid target for the tag being moved
        return canMoveNodeToTarget(item.nodePath, data.nodePath || []);
      }
      return true;
    },
    drop: (item, monitor) => {
      if (monitor.getItemType() === "MOVE_TAG_NODE") {
        // Handle moving an existing tag node to root level
        if (data.onMoveTagNode) {
          data.onMoveTagNode(item.nodeData, data);
        }
        return { moved: true };
      } else {
        // Handle adding a new tag
        const tag = item.tag;
        if (tag) {
          data.onAddNode(data, "root");
          data.handleAddNodeApplied(data, tag);
        }
        return { dropped: true };
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop() && monitor.isOver(),
    }),
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If dropdown isn't open, don't do anything
      if (!showDataSliceDropdown) return;

      // Check if click is inside dropdown or button
      const isClickInside =
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownButtonRef.current &&
          dropdownButtonRef.current.contains(event.target));

      // If click is outside both elements, close the dropdown
      if (!isClickInside) {
        setShowDataSliceDropdown(false);
      }
    };

    // Use mousedown to catch the event before other handlers
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [showDataSliceDropdown]);

  return (
    <div
      ref={drop}
      className={`flex flex-col items-center p-3 ${
        isOver && canDrop
          ? "bg-green-100 border-2 border-dashed border-green-400 rounded-lg p-5"
          : isOver
            ? "bg-red-50 rounded-lg p-5"
            : ""
      }`}
    >
      <div className="rounded-xl border-3 border-primary bg-primary bg-opacity-10 border border-primary/90 px-8 py-3 shadow-lg transform hover:scale-105 transition-all">
        {loadingFiles ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span>Loading...</span>
          </div>
        ) : (
          <>
            <div className="text-[2rem] font-bold text-primary text-center">
              {totalActiveFiles.toLocaleString()}
            </div>
            <span className="text-[2rem] text-primary/80 block text-center">
              Total Files
            </span>
          </>
        )}
      </div>

      <div className="flex flex-col items-center gap-2 mt-1">
        {showControls && (
          <div className="flex flex-row gap-2">
            <button
              className="hover:scale-110 transition-all flex items-center gap-2 bg-primary text-white rounded-full shadow-lg px-4 py-2"
              onClick={handleAddRootChild}
              title="Add new tag"
            >
              {nodeIsSelected ? (
                <>
                  <AiFillPlusCircle size={18} />
                  <span className="text-[1rem] font-medium">Add Tag</span>
                </>
              ) : (
                <>
                  <AiOutlinePlusCircle size={18} />
                  <span className="text-[1rem] font-medium">Add Tag</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={
          graphLayoutDirection === "horizontal"
            ? Position.Right
            : Position.Bottom
        }
        isConnectable={false}
        style={{ opacity: 0 }}
      />

      {/* Create Data Slice Modal for Root Node */}
      {showCreateDataSlice &&
        createPortal(
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => setShowCreateDataSlice(false)}
            conditions={null} // No specific conditions - all files
            fileCount={totalActiveFiles}
            discoveryGraphData={discoveryGraphData}
            selectedDiscoveryGraph={selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              setSelectedDataSlice(dataSlice);
            }}
            isGraphMode={true}
            isAllFiles={true}
          />,
          document.body,
        )}

      {/* Add dropdown animation styles */}
      <style jsx global>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-dropdownFadeIn {
          animation: dropdownFadeIn 0.2s ease-out forwards;
        }

        /* Custom scrollbar styling */
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

// Bundle Node Component
export const BundleNode = ({ data }) => {
  const {
    graphLayoutDirection,
    hoveredBundleId,
    discoveryGraphData,
    selectedDiscoveryGraph,
  } = useContext(GraphContext);
  const { setSelectedDataSlice, totalActiveFiles } = useContext(BaseContext);
  const [showCreateDataSlice, setShowCreateDataSlice] = useState(false);

  // Bundle highlights when its incoming edge is highlighted
  const isHighlighted = hoveredBundleId === data.id;

  // Ensure data has the required properties
  const valuesWithStats = data.valuesWithStats || [];
  const connectingValues = data.connectingValues || [];
  const sourceTagName = data.sourceTagName || "";

  // Calculate connection file count from valuesWithStats
  const connectionFileCount = valuesWithStats.reduce(
    (sum, stat) => sum + (stat.fileCount || 0),
    0,
  );

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (
      connectionFileCount > 0 &&
      sourceTagName &&
      connectingValues.length > 0
    ) {
      // Use setTimeout to ensure the click event completes before opening modal
      setTimeout(() => {
        setShowCreateDataSlice(true);
      }, 0);
    }
  };

  // Create conditions for data slice from the bundle's connecting values
  const createDataSliceConditions = () => {
    if (connectingValues.length > 0 && sourceTagName) {
      // Build the complete path including parent conditions and the current connecting values
      const conditions = [];

      // Add parent conditions from source node path (if available)
      if (data.sourceNodePath && data.sourceNodePath.length > 0) {
        // Parse parent conditions from the source node path
        for (let i = 0; i < data.sourceNodePath.length - 1; i += 2) {
          const tagName = data.sourceNodePath[i];
          const value = data.sourceNodePath[i + 1];

          if (tagName && value) {
            conditions.push({
              tag: {
                name: tagName,
                values: [value],
              },
            });
          }
        }
      }

      // Only use values that are active
      const activeConnectingValues = connectingValues.filter((v) => {
        return v && v !== "";
      });

      // Add the current connection (source tag with connecting values)
      conditions.push({
        tag: {
          name: sourceTagName,
          values: activeConnectingValues,
        },
      });

      // If we have multiple conditions, use AND logic; otherwise use OR
      if (conditions.length > 1) {
        return {
          condition: "OR",
          children: [
            {
              condition: "AND",
              children: conditions,
            },
          ],
        };
      } else {
        // Single condition - just use OR structure
        return {
          condition: "OR",
          children: conditions,
        };
      }
    }
    return null;
  };

  return (
    <div
      className="flex flex-col items-center"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="relative">
        {/* SVG Arrow Container */}
        <svg width="600" height="180" className="overflow-visible">
          {(() => {
            // Remove percentages in parentheses from the label
            const cleanedLabel = data.label
              .replace(/\s*\([^)]*\)\s*$/, "")
              .trim();
            const labelText = cleanedLabel;
            const svgCenterX = 300;
            const svgCenterY = 90;
            const arrowWidth = Math.max(500, labelText.length * 12 + 200);
            const arrowHeight = 120;
            const arrowHeadWidth = 30;

            // Calculate percentages with correct denominators
            const totalDatasetPercentage =
              (connectionFileCount / (totalActiveFiles || 1)) * 100;
            const parentNodePercentage = valuesWithStats.reduce(
              (sum, stat) => sum + (stat.parentPercentage || 0),
              0,
            );

            // Create arrow path centered in SVG
            const arrowPath = [
              "M",
              svgCenterX - arrowWidth / 2,
              svgCenterY - arrowHeight / 2,
              "L",
              svgCenterX + arrowWidth / 2 - arrowHeadWidth,
              svgCenterY - arrowHeight / 2,
              "L",
              svgCenterX + arrowWidth / 2,
              svgCenterY,
              "L",
              svgCenterX + arrowWidth / 2 - arrowHeadWidth,
              svgCenterY + arrowHeight / 2,
              "L",
              svgCenterX - arrowWidth / 2,
              svgCenterY + arrowHeight / 2,
              "Z",
            ].join(" ");

            return (
              <>
                {/* Arrow shape background */}
                <path
                  d={arrowPath}
                  fill={isHighlighted ? "#3B82F6" : "rgba(255, 255, 255, 0.95)"}
                  stroke={isHighlighted ? "#3B82F6" : "rgba(0, 0, 0, 0.2)"}
                  strokeWidth={isHighlighted ? 3 : 2}
                  style={{
                    transition: "all 0.2s ease-in-out",
                    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
                    cursor:
                      connectionFileCount > 0 && sourceTagName
                        ? "pointer"
                        : "default",
                  }}
                  onClick={handleClick}
                />

                {/* Connection label text - centered */}
                <text
                  x={svgCenterX}
                  y={svgCenterY - 25}
                  fill={isHighlighted ? "white" : "#1F2937"}
                  fontSize="18"
                  fontWeight="700"
                  fontFamily="system-ui, -apple-system, sans-serif"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pointer-events-none"
                  style={{ transition: "fill 0.2s ease-in-out" }}
                >
                  {labelText.length > 35
                    ? labelText.substring(0, 32) + "..."
                    : labelText}
                </text>

                {/* Clear explanation of what this bundle does - below the label */}
                <text
                  x={svgCenterX}
                  y={svgCenterY - 5}
                  fill={isHighlighted ? "rgba(255,255,255,0.9)" : "#6B7280"}
                  fontSize="13"
                  fontWeight="600"
                  fontFamily="system-ui, -apple-system, sans-serif"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pointer-events-none"
                >
                  Feeds into {data.targetCount} downstream tag
                  {data.targetCount !== 1 ? "s" : ""}
                </text>

                {/* Total Dataset Percentage - left side */}
                <g>
                  <text
                    x={svgCenterX - 150}
                    y={svgCenterY + 25}
                    fill={isHighlighted ? "white" : "#059669"}
                    fontSize="20"
                    fontWeight="700"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    {totalDatasetPercentage.toFixed(1)}%
                  </text>
                  <text
                    x={svgCenterX - 150}
                    y={svgCenterY + 42}
                    fill={isHighlighted ? "rgba(255,255,255,0.8)" : "#6B7280"}
                    fontSize="12"
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    All Data
                  </text>
                </g>

                {/* Parent Node Percentage - right side */}
                <g>
                  <text
                    x={svgCenterX + 150}
                    y={svgCenterY + 25}
                    fill={isHighlighted ? "white" : "#DC2626"}
                    fontSize="20"
                    fontWeight="700"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    {parentNodePercentage.toFixed(1)}%
                  </text>
                  <text
                    x={svgCenterX + 150}
                    y={svgCenterY + 42}
                    fill={isHighlighted ? "rgba(255,255,255,0.8)" : "#6B7280"}
                    fontSize="12"
                    fontWeight="600"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="pointer-events-none"
                  >
                    Parent
                  </text>
                </g>

                {/* File count - center */}
                {connectionFileCount > 0 && (
                  <g>
                    <text
                      x={svgCenterX}
                      y={svgCenterY + 25}
                      fill={isHighlighted ? "white" : "#4B5563"}
                      fontSize="20"
                      fontWeight="700"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="pointer-events-none"
                    >
                      {connectionFileCount.toLocaleString()}
                    </text>
                    <text
                      x={svgCenterX}
                      y={svgCenterY + 42}
                      fill={isHighlighted ? "rgba(255,255,255,0.8)" : "#6B7280"}
                      fontSize="12"
                      fontWeight="600"
                      fontFamily="system-ui, -apple-system, sans-serif"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="pointer-events-none"
                    >
                      Files
                    </text>
                  </g>
                )}
              </>
            );
          })()}
        </svg>
      </div>

      <Handle
        type="target"
        position={
          graphLayoutDirection === "horizontal" ? Position.Left : Position.Top
        }
        isConnectable={false}
        style={{ opacity: 0 }}
      />
      <Handle
        type="source"
        position={
          graphLayoutDirection === "horizontal"
            ? Position.Right
            : Position.Bottom
        }
        isConnectable={false}
        style={{ opacity: 0 }}
      />

      {/* Create Data Slice Modal */}
      {showCreateDataSlice &&
        createPortal(
          <CreateDataSlice
            isOpen={showCreateDataSlice}
            onClose={() => {
              setShowCreateDataSlice(false);
            }}
            conditions={createDataSliceConditions()}
            fileCount={connectionFileCount}
            discoveryGraphData={discoveryGraphData}
            selectedDiscoveryGraph={selectedDiscoveryGraph}
            onSuccess={(dataSlice) => {
              if (dataSlice) {
                setSelectedDataSlice(dataSlice);
              }
              setShowCreateDataSlice(false);
            }}
            isGraphMode={true}
            isAllFiles={false}
          />,
          document.body,
        )}
    </div>
  );
};

export const nodeTypes = {
  question: TagNode,
  value: ValueNode,
  root: RootNode,
  bundle: BundleNode,
};
